import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { SecurityService } from '../../services/security.service';
import { LoaderService } from '../../services/loader.service';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigSelected } from '../../interfaces/interfaces';
import { CommonModule } from '@angular/common';
import { InputComponent } from '../../shared/input/input.component';
import { SelectedComponent } from '../../shared/selected/selected.component';
import { COUNTRIES } from '../../config/constants.config';
import { RouteService } from '../../services/route.service';
import { HelpService } from '../../services/help.service';

@Component({
  selector: 'app-foreign',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SelectedComponent
  ],
  templateUrl: './foreign.component.html',
  styleUrl: './foreign.component.css'
})
export class ForeignComponent implements OnInit{

  form: FormGroup;
  inputCountryValue: string = '';
  filteredCountries: string[] = [];
  isTimeout = false;
  countries = COUNTRIES;
  nextRoute: WritableSignal<string> = signal('');

  dataSelected: WritableSignal<ConfigSelected[]> = signal([
    { label: '¿Administras recursos públicos o ejerces un cargo importante?', subLabel: '', formName: 'hasPublicJobManager', placeholder: '', type: 'text', defaultValue: false, enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
    { label: '¿Tienes Green Card?', subLabel: '', formName: 'hasGreenCard', placeholder: '', type: 'text', defaultValue: false, enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
    { label: '¿Tus padres tienen nacionalidad norteamericana?', subLabel: '', formName: 'hasNothAmericanNationality', placeholder: '', type: 'text', defaultValue: false, enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
    { label: '¿Está obligado a tributar en un país diferente a Colombia?', subLabel: '', formName: 'isObligatedTaxOtherCountry', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
    { label: '¿Es extranjero residente?', subLabel: '', formName: 'isForeignResident', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [ Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([{value: true, label: 'Si'}, {value: false, label: 'No'}])},
  ])

  constructor(
    private _router: Router,
    private _title: Title,
    private _securityService: SecurityService,
    private _loaderService: LoaderService,
    private _routeService: RouteService,
    private _helpService: HelpService
    
  ){
      this._title.setTitle('Onboarding Digital | Información Adicional');
      this.form = new FormGroup({
        hasPublicJobManager: new FormControl(null, [Validators.required]),
        hasGreenCard: new FormControl(null, [Validators.required]),
        hasNothAmericanNationality: new FormControl(null, [Validators.required]),
        isObligatedTaxOtherCountry: new FormControl(null, [Validators.required]),
        obligatedTaxCountry: new FormControl(null),
        isForeignResident: new FormControl(null, [Validators.required])
      }, this.countryValidator.bind(this) as any);
      
      // Suscribirse a cambios en isObligatedTaxOtherCountry
    this.nextRoute.set(this._routeService.getNextRoute(this._router.url) ?? '');
      this.form.get('isObligatedTaxOtherCountry')?.valueChanges.subscribe((value:boolean) => {
        this.toggleCountryValidation(value);
      });
  }

  ngOnInit(): void {
    this._loaderService.hide();
    this._helpService.show();
    this.getForeign();
    this.toggleCountryValidation(this.form.get('isObligatedTaxOtherCountry')?.value);
  }

  getForeign(){
    this._securityService.getForeign().subscribe({
      next: (response:any) => {
        this.form.patchValue(response)
        this.dataSelected.update((inputs:any) =>
            inputs.map((input:any) => ({
              ...input,
              defaultValue: response[input.formName] ?? input.defaultValue
            }))
          );
      },
      error: (error: any) => {
        console.error("ERROR: ", error);
      }
    })
  }

  toggleCountryValidation(isObligated: any) {
    const countryControl = this.form.get('obligatedTaxCountry');
    
    if (JSON.parse(isObligated) === true) {
      countryControl?.setValidators([Validators.required]);
    } else {
      countryControl?.clearValidators();
      countryControl?.setValue('');
      this.inputCountryValue = '';
    }
    
    // Actualizar validación
    countryControl?.updateValueAndValidity();
  }

  onSubmit() {
    if(this.form.get('hasPublicJobManager')?.value === true){
      this._securityService.setForeningHasPublicJobManager(this.form.value).subscribe({
        next: (response: any) => {
          this._router.navigateByUrl('foreignCheck');
        },
      });
    }else{
      this._securityService.setForeign(this.form.value).subscribe({
        next: (response: any) => {
          this._router.navigateByUrl(this.nextRoute());
        },
        error: (error: any) => {
          console.error("ERROR: ", error);
        }
      });
    }
  }

  handleValue(event: {formName:string, value: boolean}) {
      this.form.get(event.formName)?.setValue(event.value);
      if (event.formName === 'isObligatedTaxOtherCountry') {
        this.toggleCountryValidation(event.value);
      }
  }

  onSearchChange(value: string) {
    this.inputCountryValue = value;
    if (value && value.length > 1) {
      const val = value.toLowerCase();
      this.filteredCountries = this.countries.filter(c =>
        c.toLowerCase().includes(val)
      );
    } else {
      this.filteredCountries = [];
    }
  }

  selectCountry(country: string) {
    this.form.get('obligatedTaxCountry')?.setValue(country);
    this.inputCountryValue = country;
    this.filteredCountries = [];
  }

  // Validator a nivel de FormGroup
  countryValidator(control: import('@angular/forms').AbstractControl) {
    const form = control as FormGroup;
    const taxObligation = form.get('isObligatedTaxOtherCountry')?.value;
    const country = form.get('obligatedTaxCountry')?.value;

    if (taxObligation === true || taxObligation === 'true') {
      if (!country || !this.countries.includes(country)) {
        return { invalidCountry: true };
      }
    }
    return null;
  }
}